/** @jsxImportSource @emotion/react */
import { useRef } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@emotion/react';
import MVFDatePicker from '@DatePicker';
import { batch, useDispatch, useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getErrorMessage } from '../../Helpers/validation';
import {
  setComponentValidationStatus,
  updateValue,
} from '../../Lib/redux/slices/components';
import QuestionText from '../QuestionText/QuestionText';
import makeDatePickerStyling from './makeDatePickerStyling';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import useDatePickerHeightManagement from '../../Hooks/useDatePickerHeightManagement/useDatePickerHeightManagement';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const DELAY_BEFORE_OPENING = 450;

const DatePicker = ({ componentId, subheading, formId }) => {
  const dispatch = useDispatch();
  const containerRef = useRef();
  const styles = makeDatePickerStyling({ theme: useTheme() });
  const countryCode = useSelector((store) => store.formConfig.countryCode);
  const { iFrameId } = useSelector((store) => store.optionalFormConfig);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const components = useSelector((store) => store.components);
  const errorMessage = getErrorMessage(component.validation?.constraints);

  const datePickerExperimentName = useGetExperimentName({
    componentId,
    componentName: 'DatePicker',
  });

  const { value, disablePastDates } = component.input;
  let { placeholder } = component.input;
  const { displayQuestionTextInsideTextfield, text } = component.component;

  const handleChange = (newInputValue) => {
    batch(() => {
      dispatch(updateValue({ componentId, value: newInputValue }));
      dispatch(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  const { handleShowModalHeightAdjustment } = useDatePickerHeightManagement(
    containerRef,
    dispatch,
    componentId
  );

  let format = 'dd/MM/yyyy';
  if (countryCode === 'US') {
    format = 'MM/dd/yyyy';
    placeholder = 'MM/DD/YYYY';
  }

  return (
    <Box
      css={styles.datePickerRoot}
      data-testid="datepickerContainer"
      ref={containerRef}
    >
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: component,
          iFrameId,
          formId,
        })}
        subheading={renderMustache({
          template: subheading,
          components,
          currentComponent: component,
          iFrameId,
          formId,
        })}
      />
      <Box css={styles.datePickerBox}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MVFDatePicker
            id={`${componentId}`}
            disablePast={!!disablePastDates}
            errorMessage={errorMessage}
            label={displayQuestionTextInsideTextfield ? text : undefined}
            format={format}
            onChange={handleChange}
            onOpen={() =>
              handleShowModalHeightAdjustment({ isModalVisible: true })
            }
            onClose={() =>
              handleShowModalHeightAdjustment({ isModalVisible: false })
            }
            openDelay={DELAY_BEFORE_OPENING}
            placeholder={placeholder}
            value={value}
            experimentName={datePickerExperimentName}
          />
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

export default DatePicker;
